import React, { useState, useEffect } from 'react';
import {
  Modal,
  ModalDialog,
  ModalClose,
  Typography,
  Box,
  Button,
  FormControl,
  FormLabel,
  Input,
  Textarea,
  Select,
  Option,
  Alert,
  CircularProgress,
  Divider,
} from '@mui/joy';
import { Calendar, CurrencyDollar as DollarSign, FileText, User } from '@phosphor-icons/react';
import api from '../../../lib/api';

interface Contact {
  id: number;
  first_name?: string;
  last_name?: string;
  full_name?: string;
  email: string;
  company?: string;
  company_name?: string;
}

interface Opportunity {
  id: number;
  name: string;
  contact: number;
  estimated_value?: number;
}

interface ContractCreateModalProps {
  open: boolean;
  onClose: () => void;
  onSave: (contractData: any) => Promise<void>;
  contract?: any; // For editing existing contracts
}

export const ContractCreateModal: React.FC<ContractCreateModalProps> = ({
  open,
  onClose,
  onSave,
  contract
}) => {
  const [formData, setFormData] = useState({
    title: '',
    customer: '',
    opportunity: '',
    total_value: '',
    expiration_date: '',
    status: 'draft',
    contract_data: {
      description: '',
      notes: ''
    }
  });

  const [contacts, setContacts] = useState<Contact[]>([]);
  const [opportunities, setOpportunities] = useState<Opportunity[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Load data when modal opens
  useEffect(() => {
    if (open) {
      loadContacts();
      loadOpportunities();
      
      // If editing, populate form
      if (contract) {
        setFormData({
          title: contract.title || '',
          customer: contract.customer?.toString() || '',
          opportunity: contract.opportunity?.toString() || '',
          total_value: contract.total_value || '',
          expiration_date: contract.expiration_date ? 
            new Date(contract.expiration_date).toISOString().split('T')[0] : '',
          status: contract.status || 'draft',
          contract_data: {
            description: contract.contract_data?.description || '',
            notes: contract.contract_data?.notes || ''
          }
        });
      } else {
        // Reset form for new contract
        setFormData({
          title: '',
          customer: '',
          opportunity: '',
          total_value: '',
          expiration_date: '',
          status: 'draft',
          contract_data: {
            description: '',
            notes: ''
          }
        });
      }
    }
  }, [open, contract]);

  const loadContacts = async () => {
    try {
      setLoading(true);
      const response = await api.getContacts();
      // Handle both direct array and paginated responses
      const contactsData = Array.isArray(response.data)
        ? response.data
        : (response.data && typeof response.data === 'object' && 'results' in response.data && Array.isArray(response.data.results)
            ? response.data.results
            : []);
      setContacts(contactsData);
    } catch {
      // Intentionally empty
    } finally {
      setLoading(false);
    }
  };

  const loadOpportunities = async () => {
    try {
      const response = await api.getOpportunities();
      // Handle both direct array and paginated responses  
      const oppsData = Array.isArray(response.data)
        ? response.data
        : (response.data && typeof response.data === 'object' && 'results' in response.data && Array.isArray((response.data as any).results)
            ? (response.data as any).results
            : []);
      setOpportunities(oppsData);
    } catch {
      // Intentionally empty
    }
  };

  const handleInputChange = (field: string, value: any) => {
    if (field.startsWith('contract_data.')) {
      const dataField = field.split('.')[1];
      setFormData(prev => ({
        ...prev,
        contract_data: {
          ...prev.contract_data,
          [dataField]: value
        }
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [field]: value
      }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSaving(true);

    try {
      // Validate required fields
      if (!formData.title.trim()) {
        throw new Error('Contract title is required');
      }
      if (!formData.customer) {
        throw new Error('Customer selection is required');
      }

      // Prepare data for API
      const contractData: any = {
        title: formData.title.trim(),
        customer: parseInt(formData.customer),
        contract_data: formData.contract_data
      };

      // Add optional fields if provided
      if (formData.opportunity) {
        contractData.opportunity = parseInt(formData.opportunity);
      }
      if (formData.total_value) {
        contractData.total_value = parseFloat(formData.total_value);
      }
      if (formData.expiration_date) {
        contractData.expiration_date = new Date(formData.expiration_date).toISOString();
      }

      await onSave(contractData);
      onClose();
    } catch (error: any) {
      // Extract meaningful error message from API response
      let errorMessage = 'Failed to save contract';

      // The api client attaches the parsed body as `responseData`; without this
      // every validation failure surfaced as a bare "HTTP error! status: 400".
      const responseBody = error?.responseData ?? error?.response?.data;
      if (responseBody) {
        const data = responseBody;
        // Handle Django REST framework error formats
        if (typeof data === 'string') {
          errorMessage = data;
        } else if (data.detail) {
          errorMessage = data.detail;
        } else if (data.error) {
          errorMessage = data.error;
        } else if (data.non_field_errors) {
          errorMessage = Array.isArray(data.non_field_errors)
            ? data.non_field_errors.join(', ')
            : data.non_field_errors;
        } else {
          // Collect field-specific errors
          const fieldErrors = Object.entries(data)
            .filter(([key]) => key !== 'detail' && key !== 'error')
            .map(([field, errors]) => {
              const errorText = Array.isArray(errors) ? errors.join(', ') : String(errors);
              return `${field}: ${errorText}`;
            });
          if (fieldErrors.length > 0) {
            errorMessage = fieldErrors.join('; ');
          }
        }
      } else if (error instanceof Error) {
        errorMessage = error.message;
      }

      setError(errorMessage);
      console.error('Contract save error:', error);
    } finally {
      setSaving(false);
    }
  };

  const selectedOpportunity = opportunities.find(opp => opp.id === parseInt(formData.opportunity));
  const selectedCustomer = contacts.find(contact => contact.id === parseInt(formData.customer));

  return (
    <Modal open={open} onClose={onClose}>
      <ModalDialog size="lg" sx={{ maxWidth: 600, width: '90vw', maxHeight: '90vh', overflow: 'auto' }}>
        <ModalClose />
        <Typography level="h3" sx={{ mb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <FileText size={24} />
            {contract ? 'Edit Contract' : 'Create New Contract'}
          </Box>
        </Typography>
        <Typography level="body-sm" color="neutral" sx={{ mb: 3 }}>
          {contract ? 'Update contract details' : 'Create a new contract that can be sent for electronic signature'}
        </Typography>

        {error && (
          <Alert color="danger" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        <form onSubmit={handleSubmit}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {/* Contract Title */}
            <FormControl required>
              <FormLabel>Contract Title</FormLabel>
              <Input
                placeholder="e.g., Service Agreement - ABC Company"
                value={formData.title}
                onChange={(e) => handleInputChange('title', e.target.value)}
                startDecorator={<FileText size={18} />}
              />
            </FormControl>

            {/* Customer Selection */}
            <FormControl required>
              <FormLabel>Customer</FormLabel>
              <Select
                placeholder="Select a customer..."
                value={formData.customer}
                onChange={(_, value) => handleInputChange('customer', value)}
                startDecorator={<User size={18} />}
              >
                {contacts.map((contact) => (
                  <Option key={contact.id} value={contact.id.toString()}>
                    {contact.full_name || `${contact.first_name || ''} ${contact.last_name || ''}`.trim() || contact.email}
                  </Option>
                ))}
              </Select>
              {loading && contacts.length === 0 && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
                  <CircularProgress size="sm" />
                  <Typography level="body-sm">Loading contacts...</Typography>
                </Box>
              )}
            </FormControl>

            {/* Opportunity Selection (Optional) */}
            <FormControl>
              <FormLabel>Related Opportunity (Optional)</FormLabel>
              <Select
                placeholder="Link to an existing opportunity..."
                value={formData.opportunity}
                onChange={(_, value) => handleInputChange('opportunity', value)}
              >
                <Option value="">No opportunity selected</Option>
                {opportunities
                  .filter(opp => !formData.customer || opp.contact === parseInt(formData.customer))
                  .map((opportunity) => (
                    <Option key={opportunity.id} value={opportunity.id.toString()}>
                      {opportunity.name}
                      {opportunity.estimated_value && ` ($${opportunity.estimated_value.toLocaleString()})`}
                    </Option>
                  ))}
              </Select>
              {selectedCustomer && opportunities.filter(opp => opp.contact === selectedCustomer.id).length === 0 && (
                <Typography level="body-xs" color="neutral" sx={{ mt: 0.5 }}>
                  No opportunities found for selected customer
                </Typography>
              )}
            </FormControl>

            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
              {/* Contract Value */}
              <FormControl>
                <FormLabel>Contract Value</FormLabel>
                <Input
                  type="number"
                  placeholder="0.00"
                  value={formData.total_value}
                  onChange={(e) => handleInputChange('total_value', e.target.value)}
                  startDecorator={<DollarSign size={18} />}
                  slotProps={{
                    input: {
                      step: 0.01
                    }
                  }}
                />
                {selectedOpportunity?.estimated_value && (
                  <Typography level="body-xs" color="neutral" sx={{ mt: 0.5 }}>
                    Opportunity value: ${selectedOpportunity.estimated_value.toLocaleString()}
                  </Typography>
                )}
              </FormControl>

              {/* Expiration Date */}
              <FormControl>
                <FormLabel>Expiration Date</FormLabel>
                <Input
                  type="date"
                  value={formData.expiration_date}
                  onChange={(e) => handleInputChange('expiration_date', e.target.value)}
                  startDecorator={<Calendar size={18} />}
                />
              </FormControl>
            </Box>

            <Typography level="body-sm">Document status follows its sending and signing workflow.</Typography>

            <Divider sx={{ my: 1 }} />

            {/* Description */}
            <FormControl>
              <FormLabel>Description</FormLabel>
              <Textarea
                placeholder="Brief description of the contract terms and scope..."
                minRows={3}
                value={formData.contract_data.description}
                onChange={(e) => handleInputChange('contract_data.description', e.target.value)}
              />
            </FormControl>

            {/* Internal Notes */}
            <FormControl>
              <FormLabel>Internal Notes</FormLabel>
              <Textarea
                placeholder="Internal notes (not visible to customers)..."
                minRows={2}
                value={formData.contract_data.notes}
                onChange={(e) => handleInputChange('contract_data.notes', e.target.value)}
              />
            </FormControl>

            {/* Action Buttons */}
            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end', mt: 2 }}>
              <Button
                variant="outlined"
                color="neutral"
                onClick={onClose}
                disabled={saving}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                variant="solid"
                color="primary"
                loading={saving}
                disabled={saving}
              >
                {contract ? 'Save Changes' : 'Create Contract'}
              </Button>
            </Box>

            {!contract && (
              <Alert color="primary" sx={{ mt: 2 }}>
                <Typography level="body-sm">
                  After creating the contract, you'll be able to upload a PDF template and add form fields using the drag-and-drop designer.
                </Typography>
              </Alert>
            )}
          </Box>
        </form>
      </ModalDialog>
    </Modal>
  );
};